/*
 * Copyright 2025 coze-dev Authors
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { type FC } from 'react';

import {
  IntelligenceType,
  type IntelligenceData,
} from '@coze-arch/idl/intelligence_api';
import { I18n } from '@coze-arch/i18n';
import { formatDate, getFormatDateType } from '@coze-arch/bot-utils';

import { OntologyUsageChips } from '../ontology-usage-chips';
import { getOntologyAppNames } from '../../hooks/use-ontology-usage';

import styles from './index.module.less';

export interface DevelopTableProps {
  list: IntelligenceData[];
  onRowClick: (item: IntelligenceData) => void;
}

export const DevelopTable: FC<DevelopTableProps> = ({ list, onRowClick }) => (
  <div className={styles.tableWrap}>
    <table className={styles.table}>
      <thead>
        <tr>
          <th>{I18n.t('abr_home_table_name')}</th>
          <th>{I18n.t('abr_home_table_type')}</th>
          <th>{I18n.t('abr_home_table_status')}</th>
          <th>{I18n.t('abr_home_ontology_usage')}</th>
          <th>{I18n.t('abr_home_table_updated')}</th>
        </tr>
      </thead>
      <tbody>
        {list.map((item, index) => {
          const id = item.basic_info?.id ?? `${index}`;
          const name = item.basic_info?.name ?? I18n.t('abr_home_unknown');
          const isAgent = item.type === IntelligenceType.Bot;
          const isPublished = item.publish_info?.has_published;
          const updateTime = item.basic_info?.update_time;
          const ontologyNames = getOntologyAppNames(item);

          return (
            <tr
              key={id}
              className={styles.row}
              onClick={() => onRowClick(item)}
            >
              <td>
                <div className={styles.rowName}>
                  <div className={styles.rowAvatar}>
                    {isAgent ? '🤖' : '🔗'}
                  </div>
                  <div>
                    <div className={styles.rowTitle}>{name}</div>
                    <div className={styles.rowDesc}>
                      {item.basic_info?.description || '—'}
                    </div>
                  </div>
                </div>
              </td>
              <td>
                <span className={isAgent ? styles.tagBlue : styles.tagGray}>
                  {isAgent
                    ? I18n.t('abr_home_type_agent')
                    : I18n.t('abr_home_type_workflow')}
                </span>
              </td>
              <td>
                <span
                  className={isPublished ? styles.tagGreen : styles.tagOrange}
                >
                  {isPublished
                    ? I18n.t('Published_1')
                    : I18n.t('abr_home_status_draft')}
                </span>
              </td>
              <td>
                <OntologyUsageChips
                  names={ontologyNames}
                  className={styles.inlineChips}
                />
              </td>
              <td>
                {updateTime
                  ? formatDate(
                      Number(updateTime),
                      getFormatDateType(Number(updateTime)),
                    )
                  : '—'}
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  </div>
);
